import React, { useEffect } from "react";
import { useTheme } from "../../contexts/ThemeContext";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: (comment?: string) => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: "info" | "warning" | "danger" | "success";
  showCommentField?: boolean;
  commentPlaceholder?: string;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  type = "info",
  showCommentField = false,
  commentPlaceholder = "Add a comment (optional)...",
}) => {
  const { isDarkMode } = useTheme();
  const [comment, setComment] = React.useState("");

  // Reset comment when modal closes
  useEffect(() => {
    if (!isOpen) {
      setComment("");
    }
  }, [isOpen]);

  // Bloquear scroll del body cuando el modal está abierto
  useEffect(() => {
    if (isOpen) {
      // Guardar el scroll actual
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
      document.body.style.overflow = "hidden";
    } else {
      // Restaurar scroll
      const scrollY = document.body.style.top;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || "0") * -1);
      }
    }

    // Cleanup al desmontar
    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const getIconColor = () => {
    switch (type) {
      case "warning":
        return "text-yellow-600";
      case "danger":
        return "text-red-600";
      case "success":
        return "text-green-600";
      default:
        return "text-blue-600";
    }
  };

  const getIcon = () => {
    switch (type) {
      case "warning":
        return (
          <svg
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        );
      case "danger":
        return (
          <svg
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
      case "success":
        return (
          <svg
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
      default:
        return (
          <svg
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4 text-center">
        {/* Backdrop con blur */}
        <div
          className="fixed inset-0 bg-gray-900 bg-opacity-50 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div
          className={`relative transform overflow-hidden rounded-lg text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg ${
            isDarkMode ? "bg-gray-800" : "bg-white"
          }`}
        >
          <div
            className={`px-4 pb-4 pt-5 sm:p-6 sm:pb-4 ${
              isDarkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <div className="sm:flex sm:items-start">
              <div
                className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 ${
                  type === "danger"
                    ? isDarkMode
                      ? "bg-red-900/30"
                      : "bg-red-100"
                    : type === "warning"
                    ? isDarkMode
                      ? "bg-yellow-900/30"
                      : "bg-yellow-100"
                    : type === "success"
                    ? isDarkMode
                      ? "bg-green-900/30"
                      : "bg-green-100"
                    : isDarkMode
                    ? "bg-blue-900/30"
                    : "bg-blue-100"
                }`}
              >
                <div className={getIconColor()}>{getIcon()}</div>
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
                <h3
                  className={`text-lg font-semibold leading-6 ${
                    isDarkMode ? "text-gray-100" : "text-gray-900"
                  }`}
                >
                  {title}
                </h3>
                <div className="mt-2">
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {message}
                  </p>

                  {/* Comment Field */}
                  {showCommentField && (
                    <div className="mt-4">
                      <label
                        htmlFor="modal-comment"
                        className={`block text-sm font-medium mb-2 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Comment
                      </label>
                      <textarea
                        id="modal-comment"
                        rows={3}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder={commentPlaceholder}
                        className={`block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 ${
                          isDarkMode
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-orange-400 focus:border-orange-400"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500"
                        }`}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div
            className={`px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 ${
              isDarkMode ? "bg-gray-900" : "bg-gray-50"
            }`}
          >
            {onConfirm && (
              <button
                type="button"
                onClick={() => {
                  onConfirm(showCommentField ? comment : undefined);
                  setComment("");
                }}
                className={`inline-flex w-full justify-center rounded-md px-4 py-2 text-base font-semibold text-white shadow-sm sm:ml-3 sm:w-auto sm:text-sm ${
                  type === "danger"
                    ? "bg-red-600 hover:bg-red-700"
                    : type === "warning"
                    ? "bg-yellow-600 hover:bg-yellow-700"
                    : type === "success"
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {confirmText}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className={`mt-3 inline-flex w-full justify-center rounded-md px-4 py-2 text-base font-semibold shadow-sm ring-1 ring-inset sm:mt-0 sm:w-auto sm:text-sm ${
                isDarkMode
                  ? "bg-gray-700 text-gray-200 ring-gray-600 hover:bg-gray-600"
                  : "bg-white text-gray-900 ring-gray-300 hover:bg-gray-50"
              }`}
            >
              {cancelText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Modal;
